// src/components/UsageGuide.tsx
//
// Guía de uso personalizada de la org (Company.usage_guide). Markdown.
// Cada org tiene su propia receta: workflows, tools, voces/avatares
// configurados, recetas que ya funcionan, qué NO hacer. Se edita inline.

import { useCallback, useEffect, useState } from 'react';
import { BookOpen, Edit3, Save, X, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

import { getUsageGuide, updateUsageGuide } from '@/lib/api';

const EMPTY_PLACEHOLDER = `# Guía de uso

Esta organización aún no tiene una guía de uso configurada.

Una guía aquí documenta cómo **esta org** usa el sistema:
recetas que ya funcionan, voces/avatares configurados, credenciales
necesarias, qué NO hacer. La lee el agente como contexto antes de
producir cosas, y la consultan los humanos cuando entran nuevos.

Click en *Editar* para crear la guía.`;

export function UsageGuide() {
  const [content, setContent] = useState<string>('');
  const [draft, setDraft] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string>('');
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await getUsageGuide();
      const g = r.usage_guide || '';
      setContent(g);
      setDraft(g);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar la guía');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const r = await updateUsageGuide(draft);
      setContent(r.usage_guide || '');
      setDraft(r.usage_guide || '');
      setEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(content);
    setEditing(false);
    setError('');
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 p-8">
        <Loader2 className="animate-spin" size={16} />
        Cargando guía…
      </div>
    );
  }

  const showEmpty = !content && !editing;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <header className="flex items-center justify-between mb-6 pb-4 border-b border-zinc-200 dark:border-zinc-800">
        <div className="flex items-center gap-3">
          <BookOpen size={22} className="text-zinc-700 dark:text-zinc-300" />
          <div>
            <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
              Guía de uso
            </h1>
            <p className="text-sm text-zinc-500 mt-0.5">
              Documentación personalizada de cómo esta organización usa el sistema.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {saved && (
            <span className="flex items-center gap-1.5 text-sm text-emerald-600">
              <CheckCircle2 size={14} /> Guardado
            </span>
          )}
          {!editing ? (
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-zinc-900 text-white hover:bg-zinc-800 transition-colors"
            >
              <Edit3 size={14} /> Editar
            </button>
          ) : (
            <>
              <button
                onClick={cancel}
                disabled={saving}
                className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-md text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50"
              >
                <X size={14} /> Cancelar
              </button>
              <button
                onClick={save}
                disabled={saving || draft === content}
                className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                Guardar
              </button>
            </>
          )}
        </div>
      </header>

      {error && (
        <div className="mb-4 flex items-start gap-2 p-3 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm dark:bg-rose-950/30 dark:border-rose-900 dark:text-rose-300">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {editing ? (
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="w-full min-h-[60vh] p-4 font-mono text-sm leading-relaxed rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:ring-zinc-100"
          placeholder="# Guía de uso&#10;&#10;Escribe en markdown…"
          spellCheck={false}
        />
      ) : (
        <div className="prose prose-zinc dark:prose-invert max-w-none prose-headings:font-semibold prose-h1:text-2xl prose-h2:text-xl prose-h2:mt-8 prose-h3:text-lg prose-pre:bg-zinc-100 dark:prose-pre:bg-zinc-900 prose-code:text-rose-600 dark:prose-code:text-rose-400 prose-code:before:content-none prose-code:after:content-none">
          <ReactMarkdown>
            {showEmpty ? EMPTY_PLACEHOLDER : content}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
}
